import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  importKeyRaw, unwrapKey, deriveKey, decryptBuffer, decodeShareName, formatBytes, fromUrlSafeBase64
} from '../utils/crypto';
import { getPreviewType, getExt } from '../utils/previewTypes';
import { apiSharePublicGet, apiSharePublicDownload } from '../utils/api';

// Il percorso DENTRO la cartella condivisa ("Driving/1.pdf"), e il solo nome.
// Fuori dal componente perche' servono sia all'elenco sia all'anteprima.
function percorsoDi(item) { return item.relPath || decodeShareName(item.encryptedName); }
function nomeDi(item) {
  const p = percorsoDi(item);
  const taglio = p.lastIndexOf('/');
  return taglio >= 0 ? p.slice(taglio + 1) : p;
}

// Il tipo che il browser deve vedere per DISEGNARE l'immagine, non quello
// salvato con il file: chi carica via WebDAV manda application/octet-stream
// per tutto, e un blob con quel tipo dentro un <img> non viene disegnato —
// si vedrebbe l'icona di immagine rotta su file perfettamente validi.
// L'estensione e' l'unica cosa di cui ci si puo' fidare qui.
const MIME_IMMAGINI = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp',
};

// Quante immagini gia' decifrate si tengono in memoria. Servono a non
// riscaricare e ridecifrare quando si va avanti e indietro con le frecce,
// ma una condivisione puo' pesare 2,3 GB: senza un tetto, sfogliarla tutta
// riempirebbe la memoria della scheda. Dieci coprono l'andirivieni vero
// (avanti, indietro, ancora avanti) senza tenere in piedi un album intero.
const MAX_IN_MEMORIA = 10;

// Oltre questo peso lo zip nel browser non si fa. Non e' un numero prudente
// scelto a caso: lo zip si costruisce in memoria, e serve circa il doppio del
// totale — i file decifrati piu' l'archivio che cresce. Sopra il mezzo giga
// la scheda salta sui telefoni e su parecchi computer, e l'utente non vede un
// errore: vede la pagina morire. La via d'uscita sarebbe scrivere su disco
// mentre si comprime (File System Access API), che pero' Safari non
// implementa — e su iPhone ci sono troppi nostri clienti per accettarlo.
// Sopra la soglia si dice cosa fare, invece di offrire un bottone che rompe.
const LIMITE_ZIP = 500 * 1024 * 1024;

export default function SharePage({ token, fragmentKey, onHome }) {
  const { t } = useTranslation('share');
  const [status, setStatus]   = useState('loading'); // loading | password | ready | error
  const [share, setShare]     = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [shareKey, setShareKey] = useState(null);
  const [password, setPassword] = useState('');
  const [pwError, setPwError]   = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [busyItem, setBusyItem] = useState(null);

  const [zip, setZip] = useState(null);       // { fase, fatti, totali } | null
  const [zipErrore, setZipErrore] = useState('');

  // Anteprima: l'indice DENTRO l'elenco delle sole immagini, cosi' le frecce
  // saltano da immagine a immagine senza inciampare nei PDF in mezzo.
  const [anteprima, setAnteprima] = useState(null);   // numero | null
  const [urlAnteprima, setUrlAnteprima] = useState(null);
  const [caricaAnteprima, setCaricaAnteprima] = useState(false);
  const [erroreAnteprima, setErroreAnteprima] = useState('');
  const memoria = useRef(new Map()); // item.id -> objectURL gia' decifrato

  const immagini = share?.items?.filter(i => getPreviewType(nomeDi(i)) === 'image') || [];

  useEffect(() => {
    if (!token) { setStatus('error'); setErrorMsg(t('sharepage_invalid_link')); return; }
    (async () => {
      try {
        const data = await apiSharePublicGet(token);
        setShare(data);
        if (data.hasPassword) {
          setStatus('password');
        } else {
          const key = await importKeyRaw(fromUrlSafeBase64(fragmentKey || ''));
          setShareKey(key);
          setStatus('ready');
        }
      } catch (e) {
        setStatus('error');
        setErrorMsg(e.status === 410 ? t('sharepage_link_expired') : t('sharepage_link_not_found'));
      }
    })();
  }, [token]);

  // Gli objectURL vivono finche' non li si revoca a mano: senza questo,
  // aprire venti immagini e poi lasciare la pagina lascia venti blob in piedi.
  useEffect(() => {
    const cache = memoria.current;
    return () => { cache.forEach(url => URL.revokeObjectURL(url)); cache.clear(); };
  }, []);

  async function handleUnlock(e) {
    e.preventDefault();
    setPwError(''); setUnlocking(true);
    try {
      const pwKey = await deriveKey(password, share.pwSalt);
      const key = await unwrapKey(share.wrappedKey, pwKey);
      // Validate the derived key by test-decrypting the first item's header-less content lazily on download.
      setShareKey(key);
      setStatus('ready');
    } catch {
      setPwError(t('sharepage_wrong_password'));
    } finally { setUnlocking(false); }
  }

  // Scarica e decifra una volta sola. Il download e l'anteprima passano
  // entrambi di qui: la decifratura e' la stessa, cambia solo cosa se ne fa.
  async function scaricaEDecifra(item, tipoMime) {
    const blob = await apiSharePublicDownload(token, item.id);
    const plainBuffer = await decryptBuffer(shareKey, await blob.arrayBuffer(), item.iv);
    return new Blob([plainBuffer], { type: tipoMime || item.mimeType || 'application/octet-stream' });
  }

  async function urlDiImmagine(item) {
    const gia = memoria.current.get(item.id);
    if (gia) return gia;
    const blob = await scaricaEDecifra(item, MIME_IMMAGINI[getExt(nomeDi(item))]);
    const url = URL.createObjectURL(blob);
    memoria.current.set(item.id, url);
    if (memoria.current.size > MAX_IN_MEMORIA) {
      const piuVecchia = memoria.current.keys().next().value;
      URL.revokeObjectURL(memoria.current.get(piuVecchia));
      memoria.current.delete(piuVecchia);
    }
    return url;
  }

  // Carica l'immagine dell'indice corrente. `annullato` evita che una
  // risposta lenta sovrascriva un'immagine piu' recente se si sfoglia veloce.
  useEffect(() => {
    if (anteprima === null || !shareKey) return;
    const item = immagini[anteprima];
    if (!item) return;
    let annullato = false;
    setErroreAnteprima(''); setUrlAnteprima(null); setCaricaAnteprima(true);
    (async () => {
      try {
        const url = await urlDiImmagine(item);
        if (!annullato) setUrlAnteprima(url);
      } catch (e) {
        if (!annullato) setErroreAnteprima(e.message || String(e));
      } finally {
        if (!annullato) setCaricaAnteprima(false);
      }
    })();
    return () => { annullato = true; };
  }, [anteprima, shareKey]);

  useEffect(() => {
    if (anteprima === null) return;
    const tasto = (e) => {
      if (e.key === 'Escape') setAnteprima(null);
      if (e.key === 'ArrowRight' && immagini.length > 1) setAnteprima(i => (i + 1) % immagini.length);
      if (e.key === 'ArrowLeft'  && immagini.length > 1) setAnteprima(i => (i - 1 + immagini.length) % immagini.length);
    };
    window.addEventListener('keydown', tasto);
    return () => window.removeEventListener('keydown', tasto);
  }, [anteprima, immagini.length]);

  function apriAnteprima(item) {
    const i = immagini.findIndex(x => x.id === item.id);
    if (i >= 0) setAnteprima(i);
  }

  async function handleDownload(item) {
    setBusyItem(item.id);
    try {
      const plainBlob = await scaricaEDecifra(item);
      const filename = nomeDi(item);
      const url = URL.createObjectURL(plainBlob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert(share?.hasPassword ? t('sharepage_download_wrong_password') : t('sharepage_download_failed_prefix') + e.message);
    } finally { setBusyItem(null); }
  }

  async function scaricaTutto() {
    setZipErrore('');
    setZip({ fase: 'decifra', fatti: 0, totali: share.items.length });
    try {
      // Caricato solo al clic: sono ~100 KB che chi apre la pagina per
      // scaricare un file solo non ha motivo di pagare.
      const { default: JSZip } = await import('jszip');
      const archivio = new JSZip();

      for (const item of share.items) {
        const blob = await scaricaEDecifra(item);
        // percorsoDi conserva le sottocartelle ("Driving/1.pdf"): JSZip le
        // ricrea dentro l'archivio senza che serva dichiararle.
        archivio.file(percorsoDi(item), blob);
        setZip(z => ({ ...z, fatti: z.fatti + 1 }));
      }

      setZip({ fase: 'comprime', fatti: 0, totali: 100 });
      // STORE e non DEFLATE: quasi tutto quello che si condivide qui e' gia'
      // compresso (JPG, PNG, PDF, MP4). Comprimere di nuovo non guadagna
      // spazio e raddoppia il tempo su un telefono.
      const finale = await archivio.generateAsync(
        { type: 'blob', compression: 'STORE' },
        m => setZip(z => (z && z.fase === 'comprime' ? { ...z, fatti: Math.round(m.percent) } : z))
      );

      const url = URL.createObjectURL(finale);
      const a = document.createElement('a');
      a.href = url; a.download = `${share.name || 'download'}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setZipErrore(e.message || String(e));
    } finally {
      setZip(null);
    }
  }

  const pesoTotale = share?.items?.reduce((s, i) => s + (i.size || 0), 0) || 0;
  const zipTroppoGrande = pesoTotale > LIMITE_ZIP;

  const itemAperto = anteprima !== null ? immagini[anteprima] : null;

  return (
    <div className="share-page-root">
      <div className="share-page-header">
        <span className="vz-logo-icon">🔐</span><span className="vz-logo-text">LifetimeCloud</span>
      </div>

      <div className="share-page-card">
        {status === 'loading' && <p className="share-muted">{t('sharepage_loading')}</p>}

        {status === 'error' && (
          <>
            <h3 className="vz-modal-title">{t('sharepage_link_unavailable_title')}</h3>
            <p className="share-muted">{errorMsg}</p>
          </>
        )}

        {status === 'password' && (
          <form onSubmit={handleUnlock}>
            <h3 className="vz-modal-title">{t('sharepage_password_required_title')}</h3>
            <p className="share-muted" style={{ margin: '4px 0 12px' }}>
              <strong style={{ color: 'var(--text)' }}>{share?.name}</strong> {t('sharepage_password_protected_suffix')}
            </p>
            <input className="vz-modal-input" type="password" autoFocus placeholder={t('sharepage_password_placeholder')}
              value={password} onChange={e => setPassword(e.target.value)} />
            {pwError && <p className="vz-modal-err">{pwError}</p>}
            <div className="vz-modal-btns" style={{ marginTop: 12 }}>
              <button className="vz-btn vz-btn-primary" type="submit" disabled={unlocking || !password}>
                {unlocking ? t('sharepage_unlocking_btn') : t('sharepage_unlock_btn')}
              </button>
            </div>
          </form>
        )}

        {status === 'ready' && share && (
          <>
            <h3 className="vz-modal-title">{share.type === 'folder' ? '📁' : '📄'} {share.name}</h3>
            <p className="share-muted" style={{ margin: '2px 0 12px' }}>
              {t('sharepage_item_count', { count: share.items.length })} · {t('sharepage_end_to_end_encrypted')}
            </p>
            {share.items.length > 1 && (
              <div style={{ margin: '0 0 12px' }}>
                {zipTroppoGrande ? (
                  <p className="share-muted" style={{ margin: 0 }}>
                    {t('sharepage_zip_too_big', { size: formatBytes(pesoTotale) })}
                  </p>
                ) : (
                  <>
                    <button className="vz-btn vz-btn-primary" disabled={!!zip} onClick={scaricaTutto}>
                      {zip ? t('sharepage_zip_working_btn') : t('sharepage_zip_all_btn', { size: formatBytes(pesoTotale) })}
                    </button>
                    {zip && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ height: 6, borderRadius: 3, background: 'var(--bg)', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${Math.round((zip.fatti / (zip.totali || 1)) * 100)}%`,
                            background: 'var(--green)', transition: 'width .2s',
                          }} />
                        </div>
                        <p className="share-muted" style={{ margin: '4px 0 0' }}>
                          {zip.fase === 'decifra'
                            ? t('sharepage_zip_progress', { done: zip.fatti, total: zip.totali })
                            : t('sharepage_zip_compressing', { percent: zip.fatti })}
                        </p>
                      </div>
                    )}
                    {zipErrore && <p className="vz-modal-err">{t('sharepage_zip_failed')}</p>}
                  </>
                )}
              </div>
            )}
            <div className="move-folder-list" style={{ maxHeight: 420 }}>
              {share.items.map(item => {
                // relPath e' il percorso DENTRO la cartella condivisa
                // ("Driving/1.pdf"), non il solo nome: si separa in nome +
                // cartella, perche' su una riga sola il percorso intero
                // spinge fuori il nome — che e' l'unica cosa che serve per
                // riconoscere il file. La cartella resta, come sottotitolo,
                // per chi condivide alberi con nomi ripetuti (IMG_2300.JPG
                // in tre sottocartelle diverse).
                const percorso = percorsoDi(item);
                const nome     = nomeDi(item);
                const taglio   = percorso.lastIndexOf('/');
                const cartella = taglio >= 0 ? percorso.slice(0, taglio) : '';
                const immagine = getPreviewType(nome) === 'image';
                return (
                  <div key={item.id} className="share-list-item">
                    <span
                      style={{ fontSize: 18, cursor: immagine ? 'pointer' : 'default' }}
                      onClick={immagine ? () => apriAnteprima(item) : undefined}
                    >
                      {immagine ? '🖼' : '📄'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: 'var(--text)', fontSize: 13.5, fontWeight: 600,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          cursor: immagine ? 'pointer' : 'default',
                          textDecoration: immagine ? 'underline dotted' : 'none',
                        }}
                        title={percorso}
                        onClick={immagine ? () => apriAnteprima(item) : undefined}
                      >
                        {nome}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cartella ? cartella + ' · ' : ''}{formatBytes(item.size)}
                      </div>
                    </div>
                    <button className="vz-btn vz-btn-sm vz-btn-primary" disabled={busyItem === item.id} onClick={() => handleDownload(item)}>
                      {busyItem === item.id ? '⏳' : t('sharepage_download_btn')}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {itemAperto && (
        <div className="pv-overlay" onClick={() => setAnteprima(null)}>
          <div className="pv-modal" onClick={e => e.stopPropagation()}>
            <div className="pv-header">
              <span className="pv-filename">{nomeDi(itemAperto)}</span>
              <div className="pv-header-actions">
                {immagini.length > 1 && (
                  <>
                    <button className="pv-btn-dl" title={t('sharepage_preview_prev')}
                      onClick={() => setAnteprima(i => (i - 1 + immagini.length) % immagini.length)}>‹</button>
                    <span className="share-muted">{anteprima + 1}/{immagini.length}</span>
                    <button className="pv-btn-dl" title={t('sharepage_preview_next')}
                      onClick={() => setAnteprima(i => (i + 1) % immagini.length)}>›</button>
                  </>
                )}
                {urlAnteprima && (
                  <a className="pv-btn-dl" href={urlAnteprima} download={nomeDi(itemAperto)}>⬇ {t('sharepage_download_btn')}</a>
                )}
                <button className="pv-btn-close" title={t('sharepage_preview_close')} onClick={() => setAnteprima(null)}>✕</button>
              </div>
            </div>
            <div className="pv-content">
              {caricaAnteprima && <p className="share-muted">{t('sharepage_preview_loading')}</p>}
              {erroreAnteprima && <p className="vz-modal-err">{t('sharepage_preview_failed')}</p>}
              {urlAnteprima && !erroreAnteprima && (
                <img src={urlAnteprima} alt={nomeDi(itemAperto)}
                  style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 160px)', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
              )}
            </div>
          </div>
        </div>
      )}

      <button className="share-page-footer-link" onClick={() => onHome()}>{t('sharepage_footer_cta')}</button>
    </div>
  );
}
